import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setScrolled(window.scrollY > 50);
    });
  }

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5t5oc6m4hfebckkvn5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        alert('Thank you for subscribing! Check your email for business tips and insights.');
        setEmail('');
      }
    } catch (error) {
      alert('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#home" className={`text-sm font-medium transition-colors hover:text-primary-600 ${scrolled ? 'text-neutral-700' : 'text-white'}`}>Home</a>
              <a href="#about" className={`text-sm font-medium transition-colors hover:text-primary-600 ${scrolled ? 'text-neutral-700' : 'text-white'}`}>About</a>
              <a href="#services" className={`text-sm font-medium transition-colors hover:text-primary-600 ${scrolled ? 'text-neutral-700' : 'text-white'}`}>Services</a>
              <a href="#how-we-work" className={`text-sm font-medium transition-colors hover:text-primary-600 ${scrolled ? 'text-neutral-700' : 'text-white'}`}>How I Work</a>
              <a href="#testimonials" className={`text-sm font-medium transition-colors hover:text-primary-600 ${scrolled ? 'text-neutral-700' : 'text-white'}`}>Testimonials</a>
              <a href="#contact" className={`text-sm font-medium transition-colors hover:text-primary-600 ${scrolled ? 'text-neutral-700' : 'text-white'}`}>Contact</a>
              <a 
                href="https://calendly.com/meghan-metabodywork/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap"
              >
                Book Free Discovery Call
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center"
            >
              <i className={`ri-menu-line text-2xl ${scrolled ? 'text-neutral-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
              <div className="flex flex-col gap-4 p-4">
                <a href="#home" className="text-sm font-medium text-neutral-700 hover:text-primary-600">Home</a>
                <a href="#about" className="text-sm font-medium text-neutral-700 hover:text-primary-600">About</a>
                <a href="#services" className="text-sm font-medium text-neutral-700 hover:text-primary-600">Services</a>
                <a href="#how-we-work" className="text-sm font-medium text-neutral-700 hover:text-primary-600">How I Work</a>
                <a href="#testimonials" className="text-sm font-medium text-neutral-700 hover:text-primary-600">Testimonials</a>
                <a href="#contact" className="text-sm font-medium text-neutral-700 hover:text-primary-600">Contact</a>
                <a 
                  href="https://calendly.com/meghan-metabodywork/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-primary-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap text-center"
                >
                  Book Free Discovery Call
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/5921c3d285a3988599de9d054286065d/d5c381ecfbca307b8e242349e2d212ee.jpeg"
            alt="Wellness Environment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight">
              Massage &amp; Wellness Business Consulting
            </h1>
            <p className="text-lg sm:text-xl text-white/95 mb-8 leading-relaxed">
              Personalized business consulting and strategy for massage therapists and wellness professionals who want to build thriving practices—without burnout.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://calendly.com/meghan-metabodywork/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary-700 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-primary-800 transition-colors whitespace-nowrap text-center"
              >
                Start Your Growth Journey
              </a>
              <a 
                href="#services"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-white hover:text-primary-700 transition-colors whitespace-nowrap text-center"
              >
                Explore Services
              </a>
            </div>
            <div className="flex items-center gap-3 mt-8">
              <span className="text-white/90 text-sm">META Bodywork • Founded by Meghan Kenny</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <i className="ri-hand-heart-line text-5xl text-primary-600"></i>
            </div>
            <h2 className="text-5xl font-serif font-bold text-neutral-900 mb-4">Meet Meghan Kenny</h2>
          </div>

          <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
            <div className="grid md:grid-cols-5 gap-0">
              <div className="md:col-span-2">
                <img 
                  src="https://static.readdy.ai/image/5921c3d285a3988599de9d054286065d/e1f98dfea4cd46da5be3d9e21b49f01b.jpeg"
                  alt="Meghan Kenny - Founder of Meta Bodywork"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="md:col-span-3 p-12">
                <h3 className="text-3xl font-bold text-neutral-900 mb-6">Founder, LMT, & Business Consultant</h3>
                <div className="space-y-4 text-base text-neutral-700 leading-relaxed">
                  <p>
                    <strong>Meghan Kenny</strong> is a licensed massage therapist and founder of META Bodywork, a renowned massage studio in Jersey City that has been recognized as "Best Massage in Jersey City" multiple times since its founding in 2020.
                  </p>
                  <p>
                    With 15+ years in the wellness industry, Meghan has been featured on CNN and Cheddar News as an expert in massage and stress management. She launched META Bodywork in 2020 as a solo practitioner and grew it into a thriving practice with a team of 10 licensed massage therapists—proving that with vision and dedication, anyone can build a successful massage and wellness business.
                  </p>
                  <p>
                    "I work one-on-one with massage therapists and wellness practitioners to help them grow businesses that support both their clients and their lives. Whether you're just getting started or refining an established practice, I offer practical guidance, thoughtful strategy, and grounded support tailored to your unique vision."
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="bg-primary-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-primary-700 mb-1">15+</div>
                    <div className="text-xs text-neutral-600">Years Experience</div>
                  </div>
                  <div className="bg-primary-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-primary-700 mb-1">2020</div>
                    <div className="text-xs text-neutral-600">Studio Founded</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-serif font-bold text-neutral-900 mb-6">Mission</h2>
            <p className="text-xl text-neutral-700 leading-relaxed mb-8">
              My mission is to help you create a thriving pracitce that also meets your needs—so you can serve your clients with confidence and ease.
            </p>
          </div>

          <div className="bg-primary-50 rounded-3xl p-12">
            <div className="space-y-6 text-base text-neutral-700 leading-relaxed">
              <p>
                The practitioners I work with work smarter, not harder, and build businesses that leave room for rest, creativity, and life outside of work.
              </p>
              <p>
                Together, we clarify your vision and create a customized, realistic plan that's easy to implement. Our work provides personalized feedback, proven strategies, practical resources, accountability, and structure—so you can move forward with confidence and momentum.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-neutral-900 mb-4">What I Do</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              I work one-on-one with massage therapists and wellness practitioners to help them grow businesses that support both their clients and their lives.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Service 1 */}
            <div className="bg-white border border-neutral-200 rounded-2xl p-12 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-rocket-line text-5xl text-primary-700"></i>
              </div>
              <h3 className="text-3xl font-bold text-neutral-900 mb-4">Launch Your Practice</h3>
              <p className="text-base text-neutral-600 leading-relaxed mb-6">
                Perfect for new practitioners ready to start their massage therapy or wellness business. Get expert guidance on location scouting, pricing strategies, marketing, and setting up systems that support sustainable growth from day one.
              </p>
              <a href="#contact" className="inline-flex items-center text-primary-700 font-semibold hover:text-primary-800 transition-colors">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            {/* Service 2 */}
            <div className="bg-white border border-neutral-200 rounded-2xl p-12 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-5xl text-primary-700"></i>
              </div>
              <h3 className="text-3xl font-bold text-neutral-900 mb-4">Scale Your Business</h3>
              <p className="text-base text-neutral-600 leading-relaxed mb-6">
                For growing practices and teams of up to 20 seeking expansion. Learn how to increase revenue, implement effective marketing strategies, build recurring revenue streams, and create systems that allow your business to grow without burning out.
              </p>
              <a href="#contact" className="inline-flex items-center text-primary-700 font-semibold hover:text-primary-800 transition-colors">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section id="how-we-work" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-serif font-bold text-neutral-900 mb-4">How I Work</h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              One-on-one consulting tailored to your unique goals and timeline
            </p>
          </div>

          {/* Process Overview */}
          <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-3xl p-8 md:p-12 mb-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-20 h-20 flex items-center justify-center flex-shrink-0 bg-white rounded-2xl shadow-md">
                <i className="ri-route-line text-4xl text-primary-700"></i>
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl font-bold text-neutral-900 mb-3">The Process</h3>
                <p className="text-base text-neutral-700 leading-relaxed">
                  I offer individualized consulting through private calls. We may work together for a few weeks or several months, depending on your goals. We begin with a <strong>free 15-minute consultation</strong> to explore your needs and determine if we're a good fit. After that, sessions are scheduled as needed.
                </p>
              </div>
            </div>
          </div>

          {/* Two Booking Options */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* New Clients Card */}
            <div className="bg-white rounded-3xl border-2 border-primary-200 overflow-hidden hover:shadow-xl transition-all duration-300">
              {/* Header */}
              <div className="bg-primary-100 px-8 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-700 rounded-full">
                    <i className="ri-compass-discover-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-primary-600 uppercase tracking-wide">Step 1</div>
                    <div className="text-lg font-bold text-neutral-900">New Clients</div>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-neutral-900 mb-2">Discovery Call</h3>
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-6">
                  <i className="ri-gift-line"></i>
                  Free • 15 Minutes
                </div>
                
                <p className="text-base text-neutral-600 leading-relaxed mb-6">
                  Let's connect! Schedule a complimentary call to discuss your goals and see if we're the right fit to work together.
                </p>
                
                {/* Session Details */}
                <div className="bg-neutral-50 rounded-xl p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <i className="ri-time-line text-primary-600"></i>
                      <span className="text-neutral-700">15 minutes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-video-line text-primary-600"></i>
                      <span className="text-neutral-700">Video call</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-primary-600"></i>
                      <span className="text-neutral-700">No commitment</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-lightbulb-line text-primary-600"></i>
                      <span className="text-neutral-700">Get clarity</span>
                    </div>
                  </div>
                </div>
                
                <a 
                  href="https://calendly.com/meghan-metabodywork/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full bg-primary-700 text-white px-6 py-4 rounded-xl text-base font-semibold hover:bg-primary-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-line text-xl"></i>
                  Book Your Free Call
                </a>
              </div>
            </div>

            {/* Returning Clients Card */}
            <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300">
              {/* Header */}
              <div className="bg-neutral-800 px-8 py-5 border-b border-neutral-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-500 rounded-full">
                    <i className="ri-vip-crown-line text-xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-accent-400 uppercase tracking-wide">Step 2</div>
                    <div className="text-lg font-bold text-white">Returning Clients</div>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-2">Power Hour Sessions</h3>
                <div className="inline-flex items-center gap-2 bg-accent-500/20 text-accent-400 px-3 py-1 rounded-full text-sm font-semibold mb-6">
                  <i className="ri-price-tag-3-line"></i>
                  $150 – $350 • Sliding Scale
                </div>
                
                <p className="text-base text-neutral-300 leading-relaxed mb-6">
                  Focused strategy sessions to keep your momentum going. We'll tackle challenges and ensure real progress toward your goals.
                </p>
                
                {/* Session Details */}
                <div className="bg-neutral-800/50 rounded-xl p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <i className="ri-time-line text-accent-400"></i>
                      <span className="text-neutral-300">60 minutes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-video-line text-accent-400"></i>
                      <span className="text-neutral-300">Video call</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-task-line text-accent-400"></i>
                      <span className="text-neutral-300">Homework</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-mail-line text-accent-400"></i>
                      <span className="text-neutral-300">Email follow-up</span>
                    </div>
                  </div>
                </div>
                
                <a 
                  href="https://calendly.com/meghan-metabodywork/60-min-consulting-call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 text-white px-6 py-4 rounded-xl text-base font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line text-xl"></i>
                  Schedule Your Session
                </a>
              </div>
            </div>
          </div>

          {/* What We Cover */}
          <div className="bg-neutral-50 rounded-3xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-neutral-900 mb-8 text-center">During Each Session, We Will:</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-search-line text-2xl text-primary-700"></i>
                </div>
                <h4 className="font-bold text-neutral-900 mb-2 text-sm">Identify Challenges</h4>
                <p className="text-xs text-neutral-600">Pinpoint what's holding you back</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-target-line text-2xl text-primary-700"></i>
                </div>
                <h4 className="font-bold text-neutral-900 mb-2 text-sm">Set Clear Goals</h4>
                <p className="text-xs text-neutral-600">Define specific objectives</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-file-list-3-line text-2xl text-primary-700"></i>
                </div>
                <h4 className="font-bold text-neutral-900 mb-2 text-sm">Create Action Plans</h4>
                <p className="text-xs text-neutral-600">Develop practical strategies</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className="ri-plant-line text-2xl text-primary-700"></i>
                </div>
                <h4 className="font-bold text-neutral-900 mb-2 text-sm">Implement Changes</h4>
                <p className="text-xs text-neutral-600">Support sustainable growth</p>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-neutral-200 text-center">
              <p className="text-sm text-neutral-600">
                Plus <strong>one follow-up email between sessions</strong> to help you stay focused and on track.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 bg-primary-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-neutral-900 mb-4">Transforming Wellness Businesses</h2>
            <p className="text-xl text-neutral-600">Real results from wellness professionals</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                ))}
              </div>
              <p className="text-lg font-serif italic text-neutral-700 mb-6 leading-relaxed">
                "Meghan's guidance was instrumental in launching my practice. Her practical strategies and industry insights helped me go from overwhelmed to confident in just a few months. I'm now fully booked with a waitlist!"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-primary-700"></i>
                </div>
                <div>
                  <div className="font-bold text-base text-neutral-900">Sarah Mitchell</div>
                  <div className="text-sm text-neutral-600">Licensed Massage Therapist, Portland</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                ))}
              </div>
              <p className="text-lg font-serif italic text-neutral-700 mb-6 leading-relaxed">
                "Working with Meghan transformed how I run my business. She helped me implement systems that doubled my revenue while actually working fewer hours. Her expertise in building recurring revenue streams was a game-changer."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-primary-700"></i>
                </div>
                <div>
                  <div className="font-bold text-base text-neutral-900">Jennifer Rodriguez</div>
                  <div className="text-sm text-neutral-600">Wellness Studio Owner, Austin</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                ))}
              </div>
              <p className="text-lg font-serif italic text-neutral-700 mb-6 leading-relaxed">
                "As someone transitioning from employee to business owner, Meghan's mentorship was invaluable. She understood the unique challenges wellness practitioners face and provided actionable solutions that actually work in the real world."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-primary-700"></i>
                </div>
                <div>
                  <div className="font-bold text-base text-neutral-900">Michael Chen</div>
                  <div className="text-sm text-neutral-600">Therapeutic Massage Specialist, Seattle</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-neutral-900 mb-4">Why Wellness Professionals Choose Meghan</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-neutral-50 rounded-2xl p-10 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-user-heart-line text-6xl text-primary-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">Personalized Guidance</h3>
              <p className="text-base text-neutral-600 leading-relaxed">
                Practical guidance, thoughtful strategy, and grounded support tailored to your unique vision and goals.
              </p>
            </div>

            <div className="bg-neutral-50 rounded-2xl p-10 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-lightbulb-line text-6xl text-primary-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">Proven Strategies</h3>
              <p className="text-base text-neutral-600 leading-relaxed">
                Customized, realistic plans that are easy to implement with practical resources and accountability.
              </p>
            </div>

            <div className="bg-neutral-50 rounded-2xl p-10 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-plant-line text-6xl text-primary-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">Sustainable Growth</h3>
              <p className="text-base text-neutral-600 leading-relaxed">
                Build a thriving business without burnout that leaves room for rest, creativity, and life outside of work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section id="resources" className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-neutral-900 mb-4">Resources for Wellness Professionals</h2>
            <p className="text-xl text-neutral-600">Free tools and insights to help you grow your practice</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="h-48 w-full">
                <img 
                  src="https://public.readdy.ai/ai/img_res/ef596723a07550c018d4910dbe5c9d14.jpg"
                  alt="Business Planning Guide"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="text-xs font-semibold text-primary-600 mb-2 uppercase tracking-wide">Free Guide</div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-3">Starting Your Massage Practice</h3>
                <p className="text-sm text-neutral-600 mb-4 leading-relaxed">
                  Coming Soon
                </p>
                <a href="#contact" className="inline-flex items-center text-primary-700 font-semibold hover:text-primary-800 transition-colors text-sm">
                  Download Guide <i className="ri-download-line ml-2"></i>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="h-48 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=financial%20calculator%20spreadsheet%20laptop%20business%20analytics%20charts%20graphs%20revenue%20growth%20professional%20workspace%20clean%20modern%20organized%20business%20metrics&width=600&height=400&seq=resource-002&orientation=landscape"
                  alt="Pricing Calculator"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="text-xs font-semibold text-primary-600 mb-2 uppercase tracking-wide">Free Tool</div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-3">Pricing Calculator</h3>
                <p className="text-sm text-neutral-600 mb-4 leading-relaxed">
                  Coming Soon
                </p>
                <a href="#contact" className="inline-flex items-center text-primary-700 font-semibold hover:text-primary-800 transition-colors text-sm">
                  Access Tool <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
              <div className="h-48 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=social%20media%20content%20creation%20phone%20camera%20wellness%20branding%20marketing%20instagram%20professional%20aesthetic%20clean%20modern%20business%20growth%20digital%20marketing&width=600&height=400&seq=resource-003&orientation=landscape"
                  alt="Marketing Templates"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="text-xs font-semibold text-primary-600 mb-2 uppercase tracking-wide">Free Templates</div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-3">Marketing Templates</h3>
                <p className="text-sm text-neutral-600 mb-4 leading-relaxed">
                  Coming Soon
                </p>
                <a href="#contact" className="inline-flex items-center text-primary-700 font-semibold hover:text-primary-800 transition-colors text-sm">
                  Get Templates <i className="ri-download-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-base text-neutral-600 mb-6">
              Follow <strong>@meghan_m_kenny</strong> on Instagram for daily business tips, industry insights, and inspiration
            </p>
            <a 
              href="https://instagram.com/meghan_m_kenny" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition-all whitespace-nowrap"
            >
              <i className="ri-instagram-line text-xl"></i>
              Follow on Instagram
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/5921c3d285a3988599de9d054286065d/71e844d522032bf8f5ad3bdccebb8f82.jpeg"
            alt="Consultation Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-black/70"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-wide uppercase" style={{ letterSpacing: '2px' }}>
            Ready to Grow Your Practice?
          </h2>
          <p className="text-xl text-white/95 mb-10 leading-relaxed">
            Schedule a complimentary 15-minute consultation to explore your needs and discover how we can work together.
          </p>
          <a 
            href="https://calendly.com/meghan-metabodywork/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-accent-500 text-white px-12 py-5 rounded-full text-lg font-semibold hover:bg-accent-600 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap"
          >
            <i className="ri-calendar-line text-2xl"></i>
            Book Your Free Consultation
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif font-bold text-neutral-900 mb-4">Get in Touch</h2>
            <p className="text-xl text-neutral-600">Have questions? I'd love to hear from you.</p>
          </div>

          <div className="bg-neutral-50 rounded-3xl p-12">
            <form 
              id="contact-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d5t5oc6m4hfebckkvn5g"
              method="POST"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target as HTMLFormElement;
                const formData = new FormData(form);
                const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                const originalText = submitButton.textContent;
                
                submitButton.disabled = true;
                submitButton.textContent = 'Sending...';
                
                try {
                  const response = await fetch('https://readdy.ai/api/form/d5t5oc6m4hfebckkvn5g', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString(),
                  });
                  
                  if (response.ok) {
                    alert('Thank you for reaching out! I\'ll get back to you within 24 hours.');
                    form.reset();
                  } else {
                    alert('Something went wrong. Please try again.');
                  }
                } catch (error) {
                  alert('Something went wrong. Please try again.');
                } finally {
                  submitButton.disabled = false;
                  submitButton.textContent = originalText;
                }
              }}
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-neutral-700 mb-2">Full Name *</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 outline-none transition-all text-sm"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-neutral-700 mb-2">Email Address *</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 outline-none transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="phone" className="block text-sm font-semibold text-neutral-700 mb-2">Phone Number</label>
                <input 
                  type="tel" 
                  id="phone"
                  name="phone"
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 outline-none transition-all text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="practice-stage" className="block text-sm font-semibold text-neutral-700 mb-2">Practice Stage *</label>
                <select 
                  id="practice-stage"
                  name="practice-stage"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 outline-none transition-all text-sm"
                >
                  <option value="">Select your current stage</option>
                  <option value="planning">Planning to start</option>
                  <option value="new">New practice (0-1 years)</option>
                  <option value="growing">Growing practice (1-3 years)</option>
                  <option value="established">Established practice (3+ years)</option>
                  <option value="team">Managing a team</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-neutral-700 mb-2">Message *</label>
                <textarea 
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 outline-none transition-all resize-none text-sm"
                  placeholder="Tell me about your practice and what you're looking to achieve..."
                  onInput={(e) => {
                    const textarea = e.target as HTMLTextAreaElement;
                    const counter = document.getElementById('char-counter');
                    if (counter) {
                      counter.textContent = `${textarea.value.length}/500`;
                    }
                  }}
                ></textarea>
                <div className="text-right text-xs text-neutral-500 mt-1">
                  <span id="char-counter">0/500</span>
                </div>
              </div>

              <button 
                type="submit"
                className="w-full bg-primary-700 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-primary-800 transition-colors whitespace-nowrap"
              >
                Send Message
              </button>
            </form>

            <div className="mt-12 pt-12 border-t border-neutral-300">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-mail-line text-3xl text-primary-600"></i>
                  </div>
                  <div className="text-sm font-semibold text-neutral-900 mb-1">Email</div>
                  <a href="mailto:meghan@metabodywork.com" className="text-sm text-neutral-600 hover:text-primary-600 transition-colors">
                    meghan@metabodywork.com
                  </a>
                </div>
                <div>
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-map-pin-line text-3xl text-primary-600"></i>
                  </div>
                  <div className="text-sm font-semibold text-neutral-900 mb-1">Location</div>
                  <div className="text-sm text-neutral-600">Jersey City, NJ</div>
                </div>
                <div>
                  <div className="w-12 h-12 flex items-center justify-center">
                    <i className="ri-instagram-line text-3xl text-primary-600"></i>
                  </div>
                  <div className="text-sm font-semibold text-neutral-900 mb-1">Instagram</div>
                  <a 
                    href="https://instagram.com/meghan_m_kenny" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-neutral-600 hover:text-primary-600 transition-colors"
                  >
                    @meghan_m_kenny
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary-900 text-white mx-6 mb-6 rounded-3xl">
        <div className="max-w-7xl mx-auto px-12 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Column 1 */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-2xl font-serif font-bold">Meghan Kenny</div>
              </div>
              <p className="text-base text-primary-100 leading-relaxed">
                Empowering massage therapists and wellness professionals to build thriving practices.
              </p>
            </div>

            {/* Column 2 */}
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <div className="space-y-3">
                <a href="#services" className="block text-sm text-primary-100 hover:text-accent-400 transition-colors">Services</a>
                <a href="#about" className="block text-sm text-primary-100 hover:text-accent-400 transition-colors">About Meghan</a>
                <a href="#how-we-work" className="block text-sm text-primary-100 hover:text-accent-400 transition-colors">How We Work</a>
                <a href="#testimonials" className="block text-sm text-primary-100 hover:text-accent-400 transition-colors">Testimonials</a>
                <a href="#contact" className="block text-sm text-primary-100 hover:text-accent-400 transition-colors">Contact</a>
              </div>
            </div>

            {/* Column 3 */}
            <div>
              <h4 className="text-lg font-bold mb-4">Connect</h4>
              <div className="space-y-3">
                <a 
                  href="https://instagram.com/meghan_m_kenny" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-primary-100 hover:text-accent-400 transition-colors"
                >
                  <i className="ri-instagram-line text-xl"></i>
                  Instagram
                </a>
                <a 
                  href="mailto:meghan@metabodywork.com"
                  className="flex items-center gap-3 text-sm text-primary-100 hover:text-accent-400 transition-colors"
                >
                  <i className="ri-mail-line text-xl"></i>
                  Email
                </a>
                <div className="flex items-center gap-3 text-sm text-primary-100">
                  <i className="ri-map-pin-line text-xl"></i>
                  Jersey City, NJ
                </div>
              </div>
            </div>

            {/* Column 4 */}
            <div>
              <h4 className="text-lg font-bold mb-4">Stay Informed</h4>
              <p className="text-sm text-primary-100 mb-4 leading-relaxed">
                Get business tips and industry insights delivered monthly.
              </p>
              <form 
                data-readdy-form
                onSubmit={handleNewsletterSubmit}
                className="flex gap-2"
              >
                <input 
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Your email address"
                  className="flex-1 px-4 py-2 rounded-lg bg-primary-800 border border-primary-700 text-white text-sm focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all"
                />
                <button 
                  type="submit"
                  className="w-10 h-10 flex items-center justify-center bg-accent-500 rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-right-line text-white"></i>
                </button>
              </form>
            </div>
          </div>

          <div className="pt-8 border-t border-primary-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-primary-100">
              © 2025 Meta Bodywork. All rights reserved.
            </div>
            <div>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary-100 hover:text-accent-400 transition-colors"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>

        {/* Watermark */}
        <div className="text-center pb-8">
          <div className="text-8xl font-bold text-primary-800/30 select-none">THRIVE</div>
        </div>
      </footer>
    </div>
  );
}
